import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import './CatalogProductCard.css';

const CatalogProductCard = ({ product }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imgLoaded, setImgLoaded] = useState(false);
    const mainImageRef = useRef(null);

    const images = product.imagenes || [];
    const hasMultipleImages = images.length > 1;

    // Lógica de gestos táctiles para el carousel (igual que PublicCatalogView)
    useEffect(() => {
        if (!imgLoaded || !hasMultipleImages) return;

        let startX = 0;
        let currentX = 0;
        let isMoving = false;
        let isDragging = false;
        let touchStartY = 0;

        const handleTouchStart = (e) => {
            startX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            currentX = startX;
            isMoving = false;
            isDragging = false;
        };

        const handleTouchMove = (e) => {
            currentX = e.touches[0].clientX;
            const diff = currentX - startX;

            if (Math.abs(diff) > 10 && !isDragging) {
                const startY = e.touches[0].clientY;
                const diffY = Math.abs(startY - touchStartY);
                const diffX = Math.abs(diff);

                if (diffX > diffY) {
                    isDragging = true;
                    e.preventDefault();
                }
            }

            if (isDragging) {
                e.preventDefault();
                isMoving = true;
            }
        };

        const handleTouchEnd = (e) => {
            if (!isMoving) return;

            const endX = e.changedTouches[0].clientX;
            const diff = startX - endX;
            const threshold = 50;

            if (Math.abs(diff) > threshold) {
                if (diff > 0 && currentImageIndex < images.length - 1) {
                    setCurrentImageIndex(prev => prev + 1);
                } else if (diff < 0 && currentImageIndex > 0) {
                    setCurrentImageIndex(prev => prev - 1);
                }
            }

            isDragging = false;
        };

        const imageElement = mainImageRef.current;
        if (imageElement) {
            imageElement.addEventListener('touchstart', handleTouchStart, { passive: true });
            imageElement.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
            imageElement.addEventListener('touchend', handleTouchEnd, { passive: true });

            return () => {
                imageElement.removeEventListener('touchstart', handleTouchStart);
                imageElement.removeEventListener('touchmove', handleTouchMove);
                imageElement.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [imgLoaded, hasMultipleImages, currentImageIndex, images.length]);

    return (
        <div className="catalog-product-card" style={{ aspectRatio: '0'}}>
            {/* Container de imagen */}
            <div className="catalog-image-wrapper">
                <div className="catalog-image-container" ref={mainImageRef}>
                    {images.length > 0 ? (
                        <div className="catalog-carousel-container">
                            <div className="catalog-image-wrapper">
                                <div
                                    className="catalog-carousel-wrapper"
                                    style={{
                                        width: `${images.length * 100}%`,
                                        transform: `translateX(-${currentImageIndex * (100 / images.length)}%)`,
                                        transition: imgLoaded ? 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                                    }}
                                >
                                    {images.map((img, index) => (
                                        <div
                                            key={index}
                                            className="catalog-carousel-slide"
                                            style={{ width: `${100 / images.length}%` }}
                                        >
                                            <img
                                                src={img}
                                                alt={`${product.nombre} ${index + 1}`}
                                                className="catalog-product-image"
                                                onLoad={() => index === 0 && setImgLoaded(true)}
                                                loading="lazy"
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Indicadores de puntos */}
                                {hasMultipleImages && (
                                    <div className="catalog-image-indicators">
                                        {images.map((_, index) => (
                                            <button
                                                key={index}
                                                className={`catalog-indicator ${index === currentImageIndex ? 'active' : ''}`}
                                                onClick={() => setCurrentImageIndex(index)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="catalog-no-image">
                            <FontAwesomeIcon icon={faImage} />
                        </div>
                    )}
                </div>
            </div>

            {/* Información del producto */}
            <div className="catalog-product-info">
                {/* Nombre del producto */}
                <h3 className="catalog-product-name">{product.nombre}</h3>

                {/* Precio del producto */}
                {product.precios && Object.keys(product.precios).length > 0 && (
                    <div className="catalog-product-price">
                        ${Math.max(...Object.values(product.precios)).toLocaleString()}
                    </div>
                )}

                {/* Información adicional */}
                <div className="catalog-product-details">
                    {product.colores && product.colores.length > 0 && (
                        <div className="catalog-product-colors">
                            <span>Colores: {product.colores.length}</span>
                        </div>
                    )}
                    {product.tallas && product.tallas.length > 0 && (
                        <div className="catalog-product-sizes">
                            <span>Tallas: {product.tallas.length}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CatalogProductCard;